import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Search,
  X,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  TrendingUp,
  AlertCircle,
  RefreshCcw,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useActiveClinic } from '@/lib/activeClinic';
import {
  usePayTransactions,
  usePayMonthlyStats,
  useExportPayTransactions,
  formatPayMoney,
  type PayTransactionsFilters,
  type PayTransactionStatus,
  type PayTransaction,
} from '@/hooks/usePayTransactions';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

// ── Constants ─────────────────────────────────────────────────────────────────

const PAY_STATUSES: Array<PayTransactionStatus | 'all'> = [
  'all',
  'succeeded',
  'failed',
  'refunded',
  'disputed',
];

const PAGE_SIZES = [10, 25, 50];

type DatePreset = 'all' | 'today' | 'this_week' | 'this_month';

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildDateRange(preset: DatePreset): { since: string; until: string } | undefined {
  if (preset === 'all') return undefined;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case 'today':
      return {
        since: today.toISOString(),
        until: new Date(today.getTime() + 86_400_000).toISOString(),
      };
    case 'this_week': {
      const start = new Date(today);
      start.setDate(today.getDate() - today.getDay());
      return { since: start.toISOString(), until: now.toISOString() };
    }
    case 'this_month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { since: start.toISOString(), until: now.toISOString() };
    }
  }
}

// ── Main Component ────────────────────────────────────────────────────────────

export function Payments({ embedded = false }: { embedded?: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClinicId } = useActiveClinic();

  const [statusFilter, setStatusFilter] = useState<PayTransactionStatus[]>([]);
  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [cursor, setCursor] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState(25);
  const [selectedTxn, setSelectedTxn] = useState<PayTransaction | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const dateRange = useMemo(() => buildDateRange(datePreset), [datePreset]);

  const filters = useMemo<PayTransactionsFilters>(
    () => ({
      status: statusFilter.length > 0 ? statusFilter : undefined,
      since:  dateRange?.since,
      until:  dateRange?.until,
      limit:  pageSize,
      cursor: cursor ?? undefined,
    }),
    [statusFilter, dateRange, pageSize, cursor],
  );

  const { data, isLoading, error } = usePayTransactions(activeClinicId, filters);
  const { data: monthlyStats, isLoading: isLoadingChart } = usePayMonthlyStats(activeClinicId);
  const exportPayTransactions = useExportPayTransactions();

  const items    = data?.items ?? [];
  const agg      = data?.aggregates;
  const hasMore  = data?.has_more ?? false;
  const nextCursor = data?.cursor ?? null;

  // Client-side search filter (on customer_email)
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return items;
    const term = searchTerm.trim().toLowerCase();
    return items.filter(
      (txn) =>
        (txn.customer_email ?? '').toLowerCase().includes(term) ||
        (txn.invoice_id ?? '').toLowerCase().includes(term) ||
        txn.id.toLowerCase().includes(term),
    );
  }, [items, searchTerm]);

  const refundRate =
    agg && agg.count > 0 ? ((agg.refund_count / agg.count) * 100).toFixed(1) : '0.0';

  const handleStatusToggle = (s: PayTransactionStatus | 'all') => {
    setCursor(null);
    if (s === 'all') {
      setStatusFilter([]);
      return;
    }
    setStatusFilter((prev) => {
      const active = prev.includes(s);
      return active ? prev.filter((x) => x !== s) : [...prev, s];
    });
  };

  const handleDatePreset = (p: DatePreset) => {
    setDatePreset(p);
    setCursor(null);
  };

  const handleSearch = (v: string) => {
    setSearchTerm(v);
  };

  const handlePageSize = (n: number) => {
    setPageSize(n);
    setCursor(null);
  };

  const handleExport = async () => {
    if (!activeClinicId) return;
    setIsExporting(true);
    setExportError(null);
    try {
      await exportPayTransactions(activeClinicId, {
        since:  dateRange?.since,
        until:  dateRange?.until,
        status: statusFilter.length > 0 ? statusFilter : undefined,
      });
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  // Recharts chart data — format kroner for axis ticks
  const chartData = useMemo(
    () =>
      (monthlyStats ?? []).map((p) => ({
        month:  p.month,
        Omsætning: Math.round(p.volume / 100),
        Gebyr:     Math.round(p.fees / 100),
      })),
    [monthlyStats],
  );

  const content = (
    <>
          {!embedded && (
            <div className="md:flex md:items-center md:justify-between">
              <div className="min-w-0 flex-1">
                <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl sm:tracking-tight">
                  {t('payments.title')}
                </h2>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {t('payments.subtitle')}
                </p>
              </div>
            </div>
          )}

          <div className={`${embedded ? 'mt-6' : 'mt-4'} flex items-center justify-end gap-2 md:ml-4 md:mt-0`}>
            {exportError && (
              <span className="text-xs text-red-500 dark:text-red-400">{exportError}</span>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleExport}
              disabled={isExporting || !activeClinicId}
              aria-label={t('payments.export_csv')}
            >
              {isExporting ? (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-1.5 h-4 w-4" />
              )}
              {t('payments.export_csv')}
            </Button>
          </div>

          {/* ── Aggregate stat cards ─────────────────────────────────────── */}
          {agg && (
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              <StatCard
                label={t('payments.stat_volume')}
                value={formatPayMoney(agg.total_volume)}
                icon={<TrendingUp className="h-5 w-5 text-indigo-500" />}
              />
              <StatCard
                label={t('payments.stat_fees')}
                value={formatPayMoney(agg.total_fees)}
                icon={<CreditCard className="h-5 w-5 text-purple-500" />}
              />
              <StatCard
                label={t('payments.stat_count')}
                value={String(agg.count)}
                icon={<TrendingUp className="h-5 w-5 text-blue-500" />}
              />
              <StatCard
                label={t('payments.stat_refund_rate')}
                value={`${refundRate}%`}
                icon={<RefreshCcw className="h-5 w-5 text-amber-500" />}
              />
            </div>
          )}

          {/* ── Recharts chart: monthly volume + fees ────────────────────── */}
          <div className="mt-6 rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-card-dark">
            <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t('payments.chart_title')}
            </h3>
            {isLoadingChart ? (
              <div className="flex h-40 items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#6b7280' }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => `${v.toLocaleString('da-DK')} kr`}
                  />
                  <Tooltip
                    formatter={(value, name) => [
                      `${(value as number).toLocaleString('da-DK')} kr`,
                      name as string,
                    ]}
                    contentStyle={{
                      fontSize: 12,
                      borderRadius: '0.375rem',
                      border: '1px solid #e5e7eb',
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="Omsætning" fill="#6366f1" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="Gebyr"     fill="#a78bfa" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* ── Filters ──────────────────────────────────────────────────── */}
          <div className="mt-6 space-y-3">
            <div className="relative max-w-md">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder={t('payments.search_placeholder')}
                className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-10 pr-3 text-sm leading-5 placeholder-gray-500 focus:border-indigo-500 focus:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => handleSearch('')}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-500"
                  aria-label={t('payments.clear_search')}
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {/* Status filter */}
              <div
                className="inline-flex rounded-md border border-gray-300 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-800"
                role="group"
                aria-label={t('payments.filter_status_label')}
              >
                {PAY_STATUSES.map((s) => {
                  const active =
                    s === 'all' ? statusFilter.length === 0 : statusFilter.includes(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => handleStatusToggle(s)}
                      aria-pressed={active}
                      className={`px-3 py-1.5 text-sm first:rounded-l-md last:rounded-r-md ${
                        active
                          ? 'bg-indigo-600 text-white'
                          : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {s === 'all'
                        ? t('payments.filter_status_all')
                        : t(`payments.status_${s}`)}
                    </button>
                  );
                })}
              </div>

              {/* Date preset filter */}
              <div
                className="inline-flex rounded-md border border-gray-300 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-800"
                role="group"
                aria-label={t('payments.filter_date_label')}
              >
                {(['all', 'today', 'this_week', 'this_month'] as DatePreset[]).map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => handleDatePreset(preset)}
                    aria-pressed={datePreset === preset}
                    className={`px-3 py-1.5 text-sm first:rounded-l-md last:rounded-r-md ${
                      datePreset === preset
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {preset === 'all'
                      ? t('payments.date_preset_all')
                      : t(`payments.date_presets.${preset}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Error ────────────────────────────────────────────────────── */}
          {error && (
            <div className="mt-6">
              <ErrorAlert message={t('payments.load_error')} />
            </div>
          )}

          {/* ── Table or empty/loading ────────────────────────────────────── */}
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState hasSearch={Boolean(searchTerm)} hasFilters={statusFilter.length > 0} />
          ) : (
            <>
              <div className="mt-6 overflow-hidden sm:rounded-md">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <Th>{t('payments.col_date')}</Th>
                        <Th>{t('payments.col_customer_email')}</Th>
                        <Th>{t('payments.col_invoice')}</Th>
                        <Th className="text-right">{t('payments.col_amount')}</Th>
                        <Th className="text-right">{t('payments.col_fee')}</Th>
                        <Th className="text-right">{t('payments.col_net')}</Th>
                        <Th>{t('payments.col_status')}</Th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t('payments.col_actions')}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
                      {filtered.map((txn) => (
                        <tr
                          key={txn.id}
                          onClick={() => setSelectedTxn(txn)}
                          className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          {/* Date */}
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="text-gray-900 dark:text-white">
                              {new Date(txn.created_at).toLocaleDateString('da-DK', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              })}
                            </div>
                            <div className="text-xs text-gray-400 dark:text-gray-500">
                              {new Date(txn.created_at).toLocaleTimeString('da-DK', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </div>
                          </td>

                          {/* Customer email */}
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-700 dark:text-gray-300">
                            {txn.customer_email ?? (
                              <span className="text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </td>

                          {/* Invoice / booking link */}
                          <td className="whitespace-nowrap px-3 py-4 font-mono text-sm">
                            {txn.invoice_id ? (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/invoices/${txn.invoice_id}`);
                                }}
                                className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                              >
                                {t('payments.view_invoice')}
                              </button>
                            ) : (
                              <span className="text-gray-400 dark:text-gray-500">—</span>
                            )}
                          </td>

                          {/* Amount */}
                          <td className="whitespace-nowrap px-3 py-4 text-right text-sm font-medium text-gray-900 dark:text-white">
                            {formatPayMoney(txn.amount, txn.currency)}
                          </td>

                          {/* Fee */}
                          <td className="whitespace-nowrap px-3 py-4 text-right text-sm text-gray-500 dark:text-gray-400">
                            {formatPayMoney(txn.fee, txn.currency)}
                          </td>

                          {/* Net */}
                          <td className="whitespace-nowrap px-3 py-4 text-right text-sm font-medium text-gray-900 dark:text-white">
                            {formatPayMoney(txn.net, txn.currency)}
                          </td>

                          {/* Status */}
                          <td className="whitespace-nowrap px-3 py-4 text-sm">
                            <PayStatusBadge status={txn.status} />
                          </td>

                          {/* Receipt link */}
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            {txn.stripe_receipt_url && (
                              <a
                                href={txn.stripe_receipt_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                                aria-label={t('payments.view_receipt')}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Pagination ─────────────────────────────────────────────── */}
              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <label htmlFor="pageSize" className="text-gray-500 dark:text-gray-400">
                    {t('payments.per_page')}:
                  </label>
                  <select
                    id="pageSize"
                    value={pageSize}
                    onChange={(e) => handlePageSize(Number(e.target.value))}
                    className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  >
                    {PAGE_SIZES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setCursor(null)}
                    disabled={cursor === null}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setCursor(nextCursor)}
                    disabled={!hasMore || !nextCursor}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
    </>
  );

  const drawer = selectedTxn ? (
    <TransactionDrawer txn={selectedTxn} onClose={() => setSelectedTxn(null)} />
  ) : null;

  if (embedded) {
    return (
      <>
        {content}
        {drawer}
      </>
    );
  }

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="max-w-none px-6">
          {content}
        </div>
      </main>
      {drawer}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-card-dark">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
        {icon}
      </div>
      <p className="mt-2 text-xl font-semibold text-gray-900 dark:text-white">{value}</p>
    </div>
  );
}

function Th({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <th
      scope="col"
      className={`px-3 py-3.5 text-left text-sm font-semibold text-gray-900 first:pl-4 dark:text-white first:sm:pl-6 ${className}`}
    >
      {children}
    </th>
  );
}

export function PayStatusBadge({ status }: { status: PayTransactionStatus }) {
  const { t } = useTranslation();
  const cls: Record<PayTransactionStatus, string> = {
    succeeded: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    failed:    'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    refunded:  'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
    disputed:  'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls[status] ?? ''}`}
    >
      {t(`payments.status_${status}`)}
    </span>
  );
}

function TransactionDrawer({
  txn,
  onClose,
}: {
  txn: PayTransaction;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('payments.drawer_title')}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-sm flex-col bg-white shadow-xl dark:bg-gray-900 sm:max-w-md"
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {t('payments.drawer_title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label={t('payments.drawer_close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Drawer body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          <dl className="space-y-4">
            <DrawerRow label={t('payments.drawer_id')}>
              <span className="font-mono text-xs text-gray-600 dark:text-gray-300 break-all">
                {txn.id}
              </span>
            </DrawerRow>

            <DrawerRow label={t('payments.col_date')}>
              {new Date(txn.created_at).toLocaleString('da-DK')}
            </DrawerRow>

            <DrawerRow label={t('payments.col_status')}>
              <PayStatusBadge status={txn.status} />
            </DrawerRow>

            <DrawerRow label={t('payments.col_amount')}>
              {formatPayMoney(txn.amount, txn.currency)}
            </DrawerRow>

            <DrawerRow label={t('payments.col_fee')}>
              {formatPayMoney(txn.fee, txn.currency)}
            </DrawerRow>

            <DrawerRow label={t('payments.col_net')}>
              <span className="font-semibold">{formatPayMoney(txn.net, txn.currency)}</span>
            </DrawerRow>

            {txn.customer_email && (
              <DrawerRow label={t('payments.col_customer_email')}>
                {txn.customer_email}
              </DrawerRow>
            )}

            {txn.description && (
              <DrawerRow label={t('payments.drawer_description')}>
                {txn.description}
              </DrawerRow>
            )}

            {txn.invoice_id && (
              <DrawerRow label={t('payments.col_invoice')}>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate(`/invoices/${txn.invoice_id}`);
                  }}
                  className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  {t('payments.view_invoice')}
                </button>
              </DrawerRow>
            )}

            {txn.stripe_receipt_url && (
              <DrawerRow label={t('payments.drawer_receipt')}>
                <a
                  href={txn.stripe_receipt_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                >
                  {t('payments.view_receipt')}
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </DrawerRow>
            )}
          </dl>
        </div>
      </div>
    </>
  );
}

function DrawerRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-gray-900 dark:text-white">{children}</dd>
    </div>
  );
}

function EmptyState({ hasSearch, hasFilters }: { hasSearch: boolean; hasFilters: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="mt-12 text-center">
      {hasSearch || hasFilters ? (
        <AlertCircle className="mx-auto h-10 w-10 text-gray-400" />
      ) : (
        <CreditCard className="mx-auto h-10 w-10 text-gray-400" />
      )}
      <h3 className="mt-4 text-base font-medium text-gray-900 dark:text-white">
        {hasSearch || hasFilters
          ? t('payments.no_payments_filtered')
          : t('payments.no_payments')}
      </h3>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {hasSearch || hasFilters
          ? t('payments.no_payments_filtered_subtitle')
          : t('payments.no_payments_subtitle')}
      </p>
    </div>
  );
}
